import { useCallback, useMemo } from "react";
import type { All8PageComponent, ArticleHeroContent } from "@bcc/core-renderers";

type Category = {
	id?: string | null;
	key?: string | null;
	title?: string | null;
	parentId?: string | null;
};

/**
 * Hook that provides breadcrumb construction utilities for ArticleHero components.
 * Handles both category-path-based and key-path-based breadcrumb generation.
 */
export function useBreadcrumbs(
	categories: Category[],
	categoryKeyMap: Map<string, string>,
	categoryTitleMap: Map<string, string>
) {
	const cleanBreadcrumbSegment = useCallback((value: string): string => {
		return (value ?? "").trim().replace(/^\/+|\/+$/g, "");
	}, []);

	const joinBreadcrumbPath = useCallback((segments: string[]): string => {
		const cleaned = segments
			.map((segment) => segment.trim().replace(/^\/+|\/+$/g, ""))
			.filter(Boolean);
		return cleaned.join("/");
	}, []);

	const resolveBreadcrumbBaseLink = useCallback((value: any): string => {
		if (!value) return "";
		if (typeof value === "string") return value.trim();
		if (typeof value === "object") {
			const external = value.external?.toString().trim();
			if (external) return external;
			const slug = value.slug?.toString().trim();
			if (slug) return slug.startsWith("/") ? slug : `/${slug}`;
		}
		return "";
	}, []);

	const buildBreadcrumbLink = useCallback((baseLink: any, categoryPath: string) => {
		const trimmed = resolveBreadcrumbBaseLink(baseLink);
		if (!trimmed) return "";
		const hashIndex = trimmed.indexOf("#");
		const withoutHash = hashIndex >= 0 ? trimmed.slice(0, hashIndex) : trimmed;
		const hash = hashIndex >= 0 ? trimmed.slice(hashIndex + 1) : "";
		const separator = withoutHash.includes("?") ? "&" : "?";
		const next = `${withoutHash}${separator}category=${categoryPath}`;
		return hash ? `${next}#${hash}` : next;
	}, [resolveBreadcrumbBaseLink]);

	const buildBreadcrumbsFromCategoryPathIds = useCallback(
		(categoryPathIds: string[], baseLink?: any) => {
			const ids = (categoryPathIds ?? []).map(String).filter(Boolean);
			if (ids.length === 0) return null;

			const segments = ids
				.map((id) => {
					const keyLabel = categoryKeyMap.get(id) ?? id;
					const keySegment = cleanBreadcrumbSegment(keyLabel);
					if (!keySegment) return null;
					const titleLabel = categoryTitleMap.get(id) ?? keyLabel;
					return { keySegment, title: titleLabel || keySegment };
				})
				.filter(Boolean) as Array<{ keySegment: string; title: string }>;

			if (segments.length === 0) return null;

			return segments.map((segment, index) => {
				const keyPath = joinBreadcrumbPath(segments.slice(0, index + 1).map((entry) => entry.keySegment));
				const slug = buildBreadcrumbLink(baseLink, keyPath);
				const crumb: { label: string; href?: { slug: string } } = { label: segment.title };
				if (slug) crumb.href = { slug };
				return crumb;
			});
		},
		[buildBreadcrumbLink, categoryKeyMap, categoryTitleMap, joinBreadcrumbPath, cleanBreadcrumbSegment]
	);

	const categoryByParentKey = useMemo(() => {
		const map = new Map<string, { id: string; title: string; key: string }>();
		categories.forEach((category) => {
			if (!category.id) return;
			const key = (category.key ?? category.title ?? category.id).trim();
			if (!key) return;
			const parentId = (category.parentId ?? "").trim();
			const mapKey = `${parentId}::${key}`.toLowerCase();
			map.set(mapKey, {
				id: category.id,
				title: (category.title ?? key).trim(),
				key
			});
		});
		return map;
	}, [categories]);

	const buildBreadcrumbsFromKeyPath = useCallback(
		(keyPath: string, baseLink?: any) => {
			const segments = (keyPath ?? "")
				.split("/")
				.map((segment) => cleanBreadcrumbSegment(segment))
				.filter(Boolean);
			if (segments.length === 0) return null;

			const resolved: Array<{ keySegment: string; title: string }> = [];
			let parentId = "";
			segments.forEach((segment) => {
				const mapKey = `${parentId}::${segment}`.toLowerCase();
				const match = categoryByParentKey.get(mapKey);
				const title = match?.title ?? segment;
				if (match?.id) {
					parentId = match.id;
				}
				resolved.push({ keySegment: segment, title });
			});

			return resolved.map((segment, index) => {
				const path = joinBreadcrumbPath(resolved.slice(0, index + 1).map((entry) => entry.keySegment));
				const slug = buildBreadcrumbLink(baseLink, path);
				const crumb: { label: string; href?: { slug: string } } = { label: segment.title };
				if (slug) crumb.href = { slug };
				return crumb;
			});
		},
		[buildBreadcrumbLink, categoryByParentKey, cleanBreadcrumbSegment, joinBreadcrumbPath]
	);

	const applyArticleHeroBreadcrumbs = useCallback(
		(component: All8PageComponent, data: Record<string, any>): All8PageComponent => {
			if (component.componentType !== "ArticleHero") return component;
			const content = (component.content ?? {}) as ArticleHeroContent;
			const source = (content.breadcrumbsSource ?? "manual").toString();
			if (source === "manual") return component;
			let auto: Array<{ label: string; href?: { slug: string } }> | null = null;
			if (source === "categories") {
				const keyPath = (content as any).breadcrumbsCategoryPath as string | undefined;
				if (keyPath && keyPath.trim()) {
					auto = buildBreadcrumbsFromKeyPath(keyPath, content.breadcrumbsLink);
				} else {
					const categoryIds = content.breadcrumbsCategoryPathIds ?? [];
					auto = buildBreadcrumbsFromCategoryPathIds(categoryIds as string[], content.breadcrumbsLink);
				}
			} else if (source === "dynamic") {
				const categoryIds = Array.isArray((data as any).categoryPathIds) ? ((data as any).categoryPathIds as any[]) : [];
				auto = buildBreadcrumbsFromCategoryPathIds(categoryIds as string[], content.breadcrumbsLink);
			}
			if (!auto || auto.length === 0) return component;
			return { ...component, content: { ...content, breadcrumbs: auto } };
		},
		[buildBreadcrumbsFromCategoryPathIds, buildBreadcrumbsFromKeyPath]
	);

	return {
		cleanBreadcrumbSegment,
		joinBreadcrumbPath,
		resolveBreadcrumbBaseLink,
		buildBreadcrumbLink,
		buildBreadcrumbsFromCategoryPathIds,
		categoryByParentKey,
		buildBreadcrumbsFromKeyPath,
		applyArticleHeroBreadcrumbs
	};
}
